/**
 * The bug-report contract — one shape and one validator, shared by the panel
 * that collects the report and the route that stores it.
 *
 * Deliberately pure and dependency-free: the client uses it to decide whether
 * Submit is enabled and what to say when a file is too big, the server uses the
 * SAME function as the authoritative check. Two copies of "is this report
 * valid?" is how a form that looks fine produces a 400 nobody can explain.
 *
 * Nothing here reads what the user MEANS. It measures objective facts about a
 * submission — empty, too long, wrong file type — which is exactly the kind of
 * thing deterministic code owns.
 */

/** Required. Long enough for a real sentence, short enough to stay a report. */
export const MAX_DESCRIPTION_LEN = 2000;
/** Optional reproduction details. */
export const MAX_STEPS_LEN = 2000;
/** Matches the chat/prompt attachment ceiling so a file accepted there is accepted here. */
export const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

export const ACCEPTED_SCREENSHOT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
] as const;

/** `accept` attribute for the file input — kept next to the list it mirrors. */
export const SCREENSHOT_ACCEPT = ACCEPTED_SCREENSHOT_TYPES.join(",");

/** Browser/device facts collected for the reporter rather than typed by them. */
export interface BugReportClientContext {
  pageUrl?: string;
  pagePath?: string;
  referrer?: string;
  deviceType?: string;
  os?: string;
  browser?: string;
  userAgent?: string;
  language?: string;
  viewport?: string;
  screen?: string;
  devicePixelRatio?: number;
  timezone?: string;
  online?: boolean;
  theme?: string;
}

/** What the panel sends and the route validates. */
export interface BugReportInput {
  /** Stable across retries of one submission — the idempotency key. */
  reportId: string;
  description: string;
  steps?: string | null;
  projectId?: string | null;
  pageUrl?: string | null;
  pagePath?: string | null;
  sessionId?: string | null;
  sessionReplayUrl?: string | null;
  context?: BugReportClientContext;
}

/** A report that passed validation: trimmed, clamped, ready to store. */
export interface ValidBugReport {
  reportId: string;
  description: string;
  steps: string | null;
  projectId: string | null;
  pageUrl: string | null;
  pagePath: string | null;
  sessionId: string | null;
  sessionReplayUrl: string | null;
  context: BugReportClientContext;
}

export type BugReportValidation =
  | { ok: true; value: ValidBugReport }
  | { ok: false; field: "reportId" | "description" | "screenshot"; error: string };

function text(value: unknown, max: number): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Clamp rather than reject: a report that says too much is still a report,
  // and losing it over a length nobody was shown would be the worse outcome.
  return trimmed.slice(0, max);
}

/**
 * Validate a submitted report. `reportId` and a non-empty description are the
 * only hard requirements — everything else is trimmed, clamped, or dropped.
 */
export function validateBugReport(input: unknown): BugReportValidation {
  const raw = (input ?? {}) as Partial<BugReportInput>;

  const reportId = text(raw.reportId, 100);
  if (!reportId) {
    return { ok: false, field: "reportId", error: "Missing report id" };
  }

  const description = text(raw.description, MAX_DESCRIPTION_LEN);
  if (!description) {
    return {
      ok: false,
      field: "description",
      error: "Tell us what went wrong so we can look into it.",
    };
  }

  return {
    ok: true,
    value: {
      reportId,
      description,
      steps: text(raw.steps, MAX_STEPS_LEN),
      projectId: text(raw.projectId, 100),
      pageUrl: text(raw.pageUrl, 2000),
      pagePath: text(raw.pagePath, 500),
      sessionId: text(raw.sessionId, 200),
      sessionReplayUrl: text(raw.sessionReplayUrl, 2000),
      context:
        raw.context && typeof raw.context === "object" ? raw.context : {},
    },
  };
}

/** Objective facts about an attached screenshot — size and declared type only. */
export function validateScreenshot(file: {
  type: string;
  size: number;
}): { ok: true } | { ok: false; error: string } {
  if (!(ACCEPTED_SCREENSHOT_TYPES as readonly string[]).includes(file.type)) {
    return { ok: false, error: "Attach a PNG, JPEG, WebP or GIF image." };
  }
  if (file.size > MAX_SCREENSHOT_BYTES) {
    return { ok: false, error: "That image is over 5 MB. Try a smaller one." };
  }
  return { ok: true };
}

/**
 * Whether the Submit control may fire. A separate export from
 * `validateBugReport` because the button asks this on every keystroke and the
 * answer must not depend on building a whole validated payload.
 */
export function canSubmitBugReport(description: string): boolean {
  return description.trim().length > 0;
}
